import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';

import parseUrl from 'url-parse';
import PageFooter from '../containers/PageFooter';
import MobileBanners from '../containers/MobileBanners';
import Page from '../containers/Page';
import HomepageWorkWithUs from '../components/HomepageWorkWithUs';
import HomepageHero from '../containers/HomepageHero';
import HomepageKnockout from '../containers/HomepageKnockout';
import HomepageFeaturedContent from '../containers/HomepageFeaturedContent';
import HomepageMarketingContent from '../containers/HomepageMarketingContent';
import CreditRedemptionStatus from '../containers/CreditRedemptionStatus';
import AnalyticsTrackers from '../containers/AnalyticsTrackers';
import NewMarketLandingPage from '../containers/NewMarketLandingPage';
import withI18n from '../components/withI18n';
import { maybeSetServerCookie, isServerCookieAllowed } from '../utils/cookies/server';
import { isClientCookieAllowed } from '../utils/cookies/client';

import { trackViewedHomepage } from '../actions/tracking/homepage';
import { requestBasket } from '../actions/basketActions';
import {
  getHomepageFeaturedContent,
  setHomepageHeroCuisineImage,
  setHomepageHeroShapeColor,
} from '../actions/homepageFeaturedContentActions';
import { requestRecentAddresses } from '../actions/locationActions';
import { showInviteModal } from '../actions/inviteActions';
import { requestConfig } from '../actions/configActions';

import isRedirectToHomeActive from '../utils/home/isRedirectToHomeActive';
import { siteDescription } from '../utils/descriptionHelper';
import { statusForState } from '../utils/pages/homepage';
import getSafe from '../utils/getSafe';
import { HOMEPAGE_REFERRER } from '../utils/constants';

import { isFeatureActive } from '../selectors/configSelectors';
import {
  NEW_MARKET_HOLDING_PAGE,
  NEW_MARKET_LANDING_PAGE,
  NEW_MARKET_LOCALE_PENDING_AR_KW,
} from '../flags/features';

import sentry from '../utils/sentry';
import CustomErrorPage from './_error';

const { captureException } = sentry();

const InviteModalContainer = dynamic(import('../containers/InviteModalContainer'), {
  ssr: true,
});

class Homepage extends Component {
  static async getPageData(context, dispatch, getState) {
    const { res, req } = context;
    const isServerSideRender = !!req;

    await Promise.all([
      dispatch(requestConfig()),
      dispatch(requestBasket()),
      dispatch(getHomepageFeaturedContent()),
      dispatch(setHomepageHeroCuisineImage()),
      dispatch(setHomepageHeroShapeColor()),
      dispatch(requestRecentAddresses()),
    ]);

    const initialState = getState();
    const { user, request } = initialState;
    const { status, error } = statusForState(initialState);
    const { link: referralLink } = user.roovite;
    const showMgmModal =
      Boolean(referralLink) && Object.keys(request.currentQuery).includes('invite');

    // Homepage redirect MVT-1164
    const isLoggedIn = Boolean(user.isLoggedIn);
    const isRedirectToHomeEnabled = isRedirectToHomeActive(initialState);
    const referralPreventedRedirect =
      Boolean(request.currentQuery.redeem_credit_token) || showMgmModal;

    if (isServerSideRender) {
      const isCookieAllowed = isServerCookieAllowed(req, 'cwa_last_home_feed');

      if (isLoggedIn && isRedirectToHomeEnabled && !referralPreventedRedirect && isCookieAllowed) {
        // Check that the url from the cookie matches the current host to prevent any malicous attempts at redirecting away from the site.
        const lastLocationUrl = getSafe(() => JSON.parse(request.lastHomeFeed).url) || '';
        const { protocol, hostname } = parseUrl(lastLocationUrl);
        const isLastLocationSafe = `${protocol}//${hostname}` === request.host;

        if (isLastLocationSafe) {
          const value = JSON.stringify({
            url: lastLocationUrl,
            referrer: HOMEPAGE_REFERRER,
          });
          // Adding the referrer into the cookie so that we can track when the user has been redirected from the homepage.
          maybeSetServerCookie(req, res, 'cwa_last_home_feed', value, { expire: 30 });
          res.redirect(302, lastLocationUrl);
        }
      }
    }

    // show the MGM modal if the user has a referral link and `invite` was a
    // querystring parameter
    if (showMgmModal) {
      await dispatch(showInviteModal());
    }

    if (res && status) {
      res.statusCode = status;

      if (error) {
        captureException(error);
      }
    }

    // Only track viewed homepage on successful homepages
    if (status === 200) {
      // Added client side cookie check until we understand why Safari sometimes
      // runs getInitialProps on the client despite no clientside routing.
      const isCookieAllowed = isServerSideRender
        ? isServerCookieAllowed(req, 'cwa_last_home_feed')
        : isClientCookieAllowed('cwa_last_home_feed');
      dispatch(
        trackViewedHomepage({
          referralPreventedRedirect: isRedirectToHomeEnabled
            ? referralPreventedRedirect
            : undefined, // Only add the tracking property if the experiment is active
          disabledRedirectCookie: isRedirectToHomeEnabled ? isCookieAllowed : undefined, // Only add the tracking property if the experiment is active
        }),
      );
    }
  }

  render() {
    const {
      statusCode,
      showNewMarketBlankPage,
      showNewMarketLandingPage,
      showNewMarketLocalePendingPage,
      tld,
      country,
      countryConfig,
    } = this.props;

    const metaDescription = siteDescription(tld);

    if (showNewMarketBlankPage || showNewMarketLandingPage || showNewMarketLocalePendingPage) {
      return (
        <Page
          pageName="new-market-landing"
          metaDescription={metaDescription}
          pagePath="/"
          alternateLocales={[]} // don't show alternate languages until we launch
        >
          <NewMarketLandingPage
            isBlank={showNewMarketBlankPage}
            localePending={showNewMarketLocalePendingPage}
          />
        </Page>
      );
    }

    if (statusCode !== 200) {
      return <CustomErrorPage statusCode={statusCode} />;
    }

    return (
      <Page pageName="homepage" metaDescription={metaDescription} pagePath="/">
        <MobileBanners page="homepage" />
        <HomepageKnockout />
        <HomepageHero />
        <InviteModalContainer />
        <CreditRedemptionStatus />

        <HomepageFeaturedContent />

        <HomepageMarketingContent />

        <HomepageWorkWithUs country={country} careersLink={countryConfig.careersLink} />

        <PageFooter />

        <AnalyticsTrackers />
      </Page>
    );
  }
}

Homepage.propTypes = {
  statusCode: PropTypes.number.isRequired,
  tld: PropTypes.string.isRequired,
  showNewMarketBlankPage: PropTypes.bool.isRequired,
  showNewMarketLandingPage: PropTypes.bool.isRequired,
  showNewMarketLocalePendingPage: PropTypes.bool.isRequired,
  country: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  countryConfig: PropTypes.shape({
    careersLink: PropTypes.string,
  }),
};

Homepage.defaultProps = {
  countryConfig: null,
};

function mapStateToProps(state) {
  const { status } = statusForState(state);
  const isArKw = state.request.currentUrl.indexOf('/ar-kw') === 0;

  return {
    statusCode: status,
    tld: state.request.country ? state.request.country.tld : '',
    showNewMarketBlankPage: isFeatureActive(state, NEW_MARKET_HOLDING_PAGE),
    showNewMarketLandingPage: isFeatureActive(state, NEW_MARKET_LANDING_PAGE),
    showNewMarketLocalePendingPage:
      isArKw && isFeatureActive(state, NEW_MARKET_LOCALE_PENDING_AR_KW),
    country: state.request.country,
    countryConfig: state.config.countries.find(
      (country) => country.id === state.request.country.tld,
    ),
  };
}

export default connect(mapStateToProps)(withI18n(Homepage));
